import {
  SignUpRequestExpanded,
  signUpCache,
} from "@faire/retailer-visitor-shared/features/SignUp/SignUpRequestData";
import { getSettingShowSignUpAnimation } from "@faire/retailer-visitor-shared/settings/getSettingShowSignUpAnimation";
import { useSettingSignUpSplit } from "@faire/retailer-visitor-shared/settings/useSettingSignUpSplit";
import { useAssignSettingSkipOwFUpsell } from "@faire/retailer-visitor-shared/settings/useSettingSkipOwFUpsell";
import { useRedirectAfterSignUp } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/useRedirectAfterSignUp";
import { useSignUpEventTracker } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/useSignUpEventTracker";
import { useSignUpQueryParams } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/useSignUpQueryParams";
import { useFullSignUpRequest } from "@faire/retailer-visitor-shared/views/SignUp/SignUpRequestContext";
import { useSignUpStepsStore } from "@faire/retailer-visitor-shared/views/SignUp/SignUpStepsContext";
import {
  Step,
  stepKeyToPathMap,
} from "@faire/retailer-visitor-shared/views/SignUp/Step";
import { getLocationOrThrow } from "@faire/web--source/common/globals/getLocation";
import { IRetailer } from "@faire/web-api--source/indigofair/data/IRetailer";
import { useCallback, useEffect } from "react";
import { useHistory } from "react-router-dom";

export type IStepper = (
  currentStep: Step,
  requestState?: Partial<SignUpRequestExpanded>,
  options?: IOptions
) => Step;

type IOptions = {
  retailer?: IRetailer;
  skipOwF?: boolean;
  startScc?: boolean;
  skipScc?: boolean;
  skipOwFUpsell?: boolean;
};

export const useSaveRequestAndGoToNextStep = (
  currentStep: Step,
  doNotTrackView?: boolean
) => {
  const history = useHistory();
  const { queryParams } = useSignUpQueryParams();
  const { trackView, trackSubmit } = useSignUpEventTracker(currentStep);
  const [{ stepper }, setSignUpStepsStore] = useSignUpStepsStore(["stepper"]);
  const [staleRequest, setRequest] = useFullSignUpRequest();
  const redirectAfterSignUp = useRedirectAfterSignUp();
  const assignSkipOwFUpsell = useAssignSettingSkipOwFUpsell();
  const isSignUpSplit = useSettingSignUpSplit();
  useEffect(() => {
    setSignUpStepsStore({ currentStep });
  }, [currentStep, setSignUpStepsStore]);

  useEffect(() => {
    if (!doNotTrackView) {
      trackView();
    }
  }, [trackView, doNotTrackView]);

  const saveAndGoToNextStep = useCallback(
    (
      request: Partial<SignUpRequestExpanded> | undefined,
      options?: IOptions
    ) => {
      const newRequest = Object.assign({}, staleRequest, request);

      const nextStep = stepper(currentStep, newRequest, options);

      const hasJustCreatedUser = options?.retailer;

      if (nextStep === Step.OWF_APPLICATION || nextStep === Step.OWF_UPSELL) {
        assignSkipOwFUpsell();
      }

      const path = stepKeyToPathMap[nextStep];
      trackSubmit();
      if (nextStep === Step.END && !getSettingShowSignUpAnimation()) {
        trackSubmit(Step.END);
        redirectAfterSignUp();
        signUpCache.clear();
      } else {
        if (request) {
          signUpCache.save(newRequest);
          setRequest(newRequest);
        }

        hasJustCreatedUser && !isSignUpSplit
          ? (getLocationOrThrow().href = path(queryParams))
          : history.push(path(queryParams));
      }
    },
    [
      staleRequest,
      stepper,
      currentStep,
      trackSubmit,
      assignSkipOwFUpsell,
      redirectAfterSignUp,
      isSignUpSplit,
      queryParams,
      history,
      setRequest,
    ]
  );

  return { saveAndGoToNextStep };
};
